import { AutoComplete, Spin } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { dashboardService, type SearchType } from '@/services/dashboard.service'
import type { SearchResult } from '@/types'

export function EntitySearch({
  type,
  placeholder,
  onSelect,
  onChange,
  value,
  className,
}: {
  type: SearchType
  placeholder: string
  value?: string
  onSelect: (value: string) => void
  onChange?: (value: string) => void
  className?: string
}) {
  const [q, setQ] = useState(value ?? '')
  const [options, setOptions] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (value != null) setQ(value)
  }, [value])

  useEffect(() => {
    if (!q || q.trim().length < 2) {
      setOptions([])
      return
    }
    let cancelled = false
    const t = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await dashboardService.search(q.trim(), type)
        if (!cancelled) setOptions(res.results)
      } catch {
        if (!cancelled) setOptions([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [q, type])

  const autoOptions = useMemo(
    () => options.map((o) => ({ value: String(o.value), label: o.label })),
    [options]
  )

  return (
    <AutoComplete
      className={className ?? 'w-full max-w-xl'}
      options={autoOptions}
      value={q}
      onChange={(v) => {
        setQ(v)
        onChange?.(v)
      }}
      onSelect={(v) => {
        setQ(v)
        onSelect(v)
        onChange?.(v)
      }}
      placeholder={placeholder}
      notFoundContent={loading ? <Spin size="small" /> : null}
      allowClear
    />
  )
}
